import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Shared/Sidebar';
import Icon from '../Shared/Icons';
import { askGemini } from '../../config/gemini';

// ── Report Tracking / Ufuatiliaji wa Ripoti ────────────────────────────
// Every student & parent, with how many reports they have, when the
// last one was written, and a clear flag for anyone never written about
// — so no child quietly slips through without follow-up.

const DAYS_STALE = 30; // a report older than this counts as "needs update"

// Tiny dependency-free SVG line chart for academic progress (0-100)
function MiniChart({ values, title }) {
  if (!values || values.length < 2) return null;
  const W = 260, H = 64, PAD = 6;
  const pts = values.map((v, i) => {
    const x = PAD + (i * (W - PAD * 2)) / (values.length - 1);
    const y = H - PAD - ((Math.max(0, Math.min(100, v)) / 100) * (H - PAD * 2));
    return `${x},${y}`;
  });
  const rising = values[values.length - 1] >= values[0];
  return (
    <div style={{ marginTop: '10px' }}>
      <div style={{ fontSize: '11px', fontWeight: '700', color: '#6B7280', marginBottom: '4px' }}>
        {title} {rising ? '↑' : '↓'}
      </div>
      <svg width={W} height={H} style={{ display: 'block', background: '#FAF9F4', borderRadius: '8px' }}>
        <polyline
          fill="none"
          stroke={rising ? '#1F6B45' : '#C62828'}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={pts.join(' ')}
        />
        {pts.map((p, i) => {
          const [x, y] = p.split(',');
          return <circle key={i} cx={x} cy={y} r="3" fill={rising ? '#14452F' : '#C62828'} />;
        })}
      </svg>
      <div style={{ fontSize: '10.5px', color: '#9CA3AF', marginTop: '2px' }}>
        Ya zamani → ya sasa · {values[0]}% → {values[values.length - 1]}%
      </div>
    </div>
  );
}

export default function ReportTracking() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [reports, setReports] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [aiSummaries, setAiSummaries] = useState({}); // personId -> text
  const [aiLoadingId, setAiLoadingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('students'); // students | parents
  const [filter, setFilter] = useState('all'); // all | none | stale | ok
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [stSnap, paSnap, reSnap, seSnap] = await Promise.all([
          getDocs(collection(db, 'students')),
          getDocs(collection(db, 'parents')),
          getDocs(collection(db, 'reports')),
          getDocs(collection(db, 'sessions')),
        ]);
        setStudents(stSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setParents(paSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setReports(reSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setSessions(seSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  function reportsFor(person, type) {
    const name = `${person.firstName || ''} ${person.lastName || ''}`.trim().toLowerCase();
    return reports
      .filter(r => {
        if (r.personId && r.personId === person.id) return true;
        // fallback for reports saved with a name but no id
        const rName = (r.personName || '').trim().toLowerCase();
        return !r.personId && rName && rName === name && (r.relatedTo || 'student') === type;
      })
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  }

  function statusOf(personReports) {
    if (personReports.length === 0) return 'none';
    const last = personReports[0].createdAt?.toDate?.();
    if (!last) return 'ok';
    const days = (Date.now() - last.getTime()) / 86400000;
    return days > DAYS_STALE ? 'stale' : 'ok';
  }

  // ── Session attendance (students only) ──────────────────────────────
  function attendanceOf(studentId) {
    const withAttendance = sessions.filter(se => se.attendance);
    if (withAttendance.length === 0) return null;
    const sorted = [...withAttendance].sort(
      (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
    );
    let present = 0, tracked = 0, recentMisses = 0, counting = true;
    for (const se of sorted) {
      const p = se.attendance.present || [];
      const a = se.attendance.absent || [];
      const e = se.attendance.excused || [];
      const isTracked = p.includes(studentId) || a.includes(studentId) || e.includes(studentId);
      if (!isTracked) continue;
      tracked++;
      if (p.includes(studentId)) { present++; if (counting) counting = false; }
      else if (counting && a.includes(studentId)) recentMisses++;
      else if (counting) counting = false; // excused breaks the miss streak
    }
    if (tracked === 0) return null;
    return { present, tracked, recentMisses };
  }

  // ── AI progress summary from the person's reports ───────────────────
  async function handleAiSummary(person, list) {
    if (list.length === 0 || aiLoadingId) return;
    setAiLoadingId(person.id);
    try {
      const corpus = list.slice(0, 8).map(r => {
        const scores = [
          r.academicProgress ? `Academic ${r.academicProgress}%` : null,
          r.behaviourProgress ? `Behaviour ${r.behaviourProgress}%` : null,
          r.homeProgress ? `Home ${r.homeProgress}%` : null,
        ].filter(Boolean).join(', ');
        return `[${r.createdAt?.toDate?.().toLocaleDateString('en-GB') || ''}] ${r.title}${scores ? ` (${scores})` : ''}: ${(r.description || '').slice(0, 500)}`;
      }).join('\n\n');
      const prompt = `You are reviewing progress reports for a person supported by Iyanna Foundation. Based ONLY on the reports below, write a concise 2-paragraph progress summary in English: paragraph 1 = overall progress and strengths (academics, behaviour, and home situation), paragraph 2 = concerns and recommended next steps. Plain text only, no markdown symbols.\n\nREPORTS:\n${corpus}`;
      const result = await askGemini(prompt);
      const clean = (result || '').replace(/[*#_`]/g, '').trim();
      setAiSummaries(prev => ({ ...prev, [person.id]: clean }));
    } catch (e) {
      console.error(e);
      setAiSummaries(prev => ({ ...prev, [person.id]: 'AI haipatikani kwa sasa. Jaribu tena baadaye.' }));
    }
    setAiLoadingId(null);
  }

  // ── Printable full report (browser Print → Save as PDF) ─────────────
  function handlePrint(person, list) {
    const name = `${person.firstName || ''} ${person.lastName || ''}`.trim();
    const esc = t => (t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br/>');
    const items = list.slice().reverse().map(r => `
      <div class="report">
        <h2>${esc(r.title)}</h2>
        <div class="meta">${esc(r.reportType)} · ${r.createdAt?.toDate?.().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) || ''}${[
          r.academicProgress ? ` · Academic: ${r.academicProgress}%` : '',
          r.behaviourProgress ? ` · Behaviour: ${r.behaviourProgress}%` : '',
          r.homeProgress ? ` · Home: ${r.homeProgress}%` : '',
        ].join('')}</div>
        <p>${esc(r.description)}</p>
        ${r.recommendations ? `<h3>Recommendations</h3><p>${esc(r.recommendations)}</p>` : ''}
      </div>`).join('');
    const html = `<!DOCTYPE html><html><head><title>${esc(name)} — Progress Reports</title>
      <style>
        body { font-family: Georgia, serif; color: #222; max-width: 720px; margin: 40px auto; padding: 0 24px; line-height: 1.8; }
        .head { border-bottom: 3px solid #14452F; padding-bottom: 16px; margin-bottom: 30px; }
        .head h1 { color: #14452F; margin: 0; font-size: 26px; }
        .head .sub { color: #777; font-size: 13px; margin-top: 4px; }
        .report { page-break-inside: avoid; margin-bottom: 34px; border-bottom: 1px solid #ddd; padding-bottom: 22px; }
        .report h2 { font-size: 18px; color: #14452F; margin: 0 0 4px; }
        .report h3 { font-size: 14px; color: #444; margin: 14px 0 4px; }
        .meta { color: #888; font-size: 12px; margin-bottom: 10px; }
        p { font-size: 13.5px; margin: 0 0 8px; }
      </style></head><body>
      <div class="head">
        <h1>Iyanna Foundation — Progress Report File</h1>
        <div class="sub">${esc(name)} · Reports: ${list.length} · Generated ${new Date().toLocaleDateString('en-GB')}</div>
      </div>
      ${items || '<p>No reports yet.</p>'}
      <script>window.onload = () => window.print();</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
    else { alert('Browser imezuia dirisha la print. Ruhusu pop-ups kwa site hii kisha ujaribu tena.'); }
  }

  const people = tab === 'students' ? students : parents;
  const rows = people
    .map(p => {
      const list = reportsFor(p, tab === 'students' ? 'student' : 'parent');
      return { person: p, list, status: statusOf(list) };
    })
    .filter(r => filter === 'all' || r.status === filter)
    .filter(r =>
      `${r.person.firstName} ${r.person.lastName}`.toLowerCase().includes(search.toLowerCase())
    )
    // most urgent first: none → stale → ok
    .sort((a, b) => {
      const order = { none: 0, stale: 1, ok: 2 };
      return order[a.status] - order[b.status];
    });

  const counts = (list => ({
    none: list.filter(r => r.status === 'none').length,
    stale: list.filter(r => r.status === 'stale').length,
    ok: list.filter(r => r.status === 'ok').length,
  }))(people.map(p => {
    const list = reportsFor(p, tab === 'students' ? 'student' : 'parent');
    return { status: statusOf(list) };
  }));

  const STATUS_UI = {
    none: { label: 'Hajaandikiwa Ripoti', bg: '#FFEBEE', color: '#C62828' },
    stale: { label: `Zaidi ya siku ${DAYS_STALE}`, bg: '#FFF3E0', color: '#9A5B00' },
    ok: { label: 'Ana Ripoti za Karibuni', bg: '#EAF3EC', color: '#14452F' },
  };

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main} className="main-content">
        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>Ufuatiliaji wa Ripoti</div>
            <div style={styles.pageSub}>
              Kila mtoto na mzazi, ripoti zake na tarehe — waliokosa ripoti wanaonekana kwanza.
            </div>
          </div>
          <button style={styles.writeBtn} onClick={() => navigate('/programs')}>
            <Icon name="report" size={15} color="#fff" /> Andika Ripoti
          </button>
        </div>

        {/* SUMMARY CARDS */}
        <div style={styles.summaryRow}>
          <div style={{ ...styles.summaryCard, borderColor: '#FFCDD2' }}>
            <div style={{ ...styles.summaryNum, color: '#C62828' }}>{counts.none}</div>
            <div style={styles.summaryLabel}>Hawajaandikiwa kabisa</div>
          </div>
          <div style={{ ...styles.summaryCard, borderColor: '#FFE0B2' }}>
            <div style={{ ...styles.summaryNum, color: '#9A5B00' }}>{counts.stale}</div>
            <div style={styles.summaryLabel}>Ripoti imepitwa (siku {DAYS_STALE}+)</div>
          </div>
          <div style={{ ...styles.summaryCard, borderColor: '#BBD8C4' }}>
            <div style={{ ...styles.summaryNum, color: '#14452F' }}>{counts.ok}</div>
            <div style={styles.summaryLabel}>Wako sawa</div>
          </div>
        </div>

        {/* TABS + FILTERS */}
        <div style={styles.controlsRow}>
          <div style={styles.tabRow}>
            {[
              { id: 'students', label: 'Wanafunzi', icon: 'students' },
              { id: 'parents', label: 'Wazazi', icon: 'parents' },
            ].map(t => (
              <button
                key={t.id}
                style={{ ...styles.tabBtn, ...(tab === t.id ? styles.tabBtnActive : {}) }}
                onClick={() => { setTab(t.id); setExpanded(null); }}
              >
                <Icon name={t.icon} size={14} /> {t.label}
              </button>
            ))}
          </div>
          <div style={styles.filterRow}>
            {[
              { id: 'all', label: 'Wote' },
              { id: 'none', label: 'Hawana Ripoti' },
              { id: 'stale', label: 'Zimepitwa' },
              { id: 'ok', label: 'Sawa' },
            ].map(fi => (
              <button
                key={fi.id}
                style={{ ...styles.filterBtn, ...(filter === fi.id ? styles.filterBtnActive : {}) }}
                onClick={() => setFilter(fi.id)}
              >
                {fi.label}
              </button>
            ))}
          </div>
        </div>

        <input
          style={styles.search}
          placeholder="Tafuta kwa jina..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {/* LIST */}
        {loading ? (
          <div style={styles.empty}>Inapakia...</div>
        ) : rows.length === 0 ? (
          <div style={styles.empty}>Hakuna anayelingana na kichujio hiki.</div>
        ) : (
          <div style={styles.list}>
            {rows.map(({ person, list, status }) => {
              const ui = STATUS_UI[status];
              const isOpen = expanded === person.id;
              const last = list[0]?.createdAt?.toDate?.();
              return (
                <div key={person.id} style={styles.card}>
                  <button style={styles.cardHead} onClick={() => setExpanded(isOpen ? null : person.id)}>
                    <div style={styles.cardHeadLeft}>
                      <div style={styles.personName}>
                        {person.firstName} {person.lastName}
                      </div>
                      <div style={styles.personMeta}>
                        {tab === 'students'
                          ? (person.class ? `Darasa: ${person.class}` : person.school || 'Mwanafunzi')
                          : (person.occupation || person.businessType || 'Mzazi')}
                      </div>
                    </div>
                    <div style={styles.cardHeadRight}>
                      <span style={{ ...styles.statusBadge, background: ui.bg, color: ui.color }}>
                        {ui.label}
                      </span>
                      <span style={styles.countPill}>
                        Ripoti: {list.length}
                      </span>
                      {last && (
                        <span style={styles.lastDate}>
                          Mwisho: {last.toLocaleDateString('en-GB')}
                        </span>
                      )}
                      <span style={styles.chevron}>{isOpen ? '▾' : '▸'}</span>
                    </div>
                  </button>

                  {isOpen && (
                    <div style={styles.reportList}>
                      {/* TOOLS: progress chart, attendance, AI summary, print */}
                      {(() => {
                        const seriesOf = (field) => list.slice().reverse()
                          .map(r => Number(r[field]))
                          .filter(v => !isNaN(v) && v > 0);
                        const academicValues = seriesOf('academicProgress');
                        const behaviourValues = seriesOf('behaviourProgress');
                        const homeValues = seriesOf('homeProgress');
                        // latest snapshot per track (works even with 1 report)
                        const latestOf = (vals) => vals.length ? vals[vals.length - 1] : null;
                        const tracks = [
                          { label: 'Kitaaluma', latest: latestOf(academicValues) },
                          { label: 'Tabia', latest: latestOf(behaviourValues) },
                          { label: 'Nyumbani', latest: latestOf(homeValues) },
                        ].filter(t => t.latest != null);
                        const att = tab === 'students' ? attendanceOf(person.id) : null;
                        return (
                          <div style={styles.toolsBox}>
                            <div style={styles.toolsBtnRow}>
                              <button
                                style={styles.aiBtn}
                                disabled={list.length === 0 || aiLoadingId === person.id}
                                onClick={() => handleAiSummary(person, list)}
                              >
                                <Icon name="ai" size={13} color="#fff" />
                                {aiLoadingId === person.id ? 'AI inasoma ripoti...' : 'Muhtasari wa AI'}
                              </button>
                              <button
                                style={styles.printBtn}
                                disabled={list.length === 0}
                                onClick={() => handlePrint(person, list)}
                              >
                                <Icon name="report" size={13} /> Ripoti Kamili (PDF/Print)
                              </button>
                            </div>
                            {att && (
                              <div style={styles.attRow}>
                                <span style={styles.attLabel}>Mahudhurio ya Sessions:</span>
                                <span style={styles.attValue}>
                                  {att.present}/{att.tracked} ({Math.round((att.present / att.tracked) * 100)}%)
                                </span>
                                {att.recentMisses >= 2 && (
                                  <span style={styles.attWarn}>
                                    Amekosa sessions {att.recentMisses} mfululizo
                                  </span>
                                )}
                              </div>
                            )}
                            {tracks.length > 0 && (
                              <div style={styles.trackRow}>
                                {tracks.map((t, i) => (
                                  <div key={i} style={styles.trackPill}>
                                    <span style={styles.trackLabel}>{t.label}</span>
                                    <span style={{
                                      ...styles.trackValue,
                                      color: t.latest >= 70 ? '#14452F' : t.latest >= 40 ? '#9A5B00' : '#C62828'
                                    }}>{t.latest}%</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div style={styles.chartsRow}>
                              {academicValues.length >= 2 && <MiniChart values={academicValues} title="Kitaaluma (Academic)" />}
                              {behaviourValues.length >= 2 && <MiniChart values={behaviourValues} title="Tabia (Behaviour)" />}
                              {homeValues.length >= 2 && <MiniChart values={homeValues} title="Nyumbani (Home)" />}
                            </div>
                            {aiSummaries[person.id] && (
                              <div style={styles.aiSummaryBox}>
                                <div style={styles.aiSummaryTitle}>
                                  <Icon name="ai" size={13} color="#14452F" /> Muhtasari wa Maendeleo (AI)
                                </div>
                                {aiSummaries[person.id].split('\n').filter(p => p.trim()).map((p, i) => (
                                  <p key={i} style={styles.aiSummaryPara}>{p}</p>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {list.length === 0 ? (
                        <div style={styles.noReports}>
                          Bado hajaandikiwa ripoti yoyote.
                          <button
                            style={styles.writeSmallBtn}
                            onClick={() => navigate('/programs')}
                          >
                            Andika ya kwanza
                          </button>
                        </div>
                      ) : (
                        list.map(r => (
                          <div key={r.id} style={styles.reportRow}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={styles.reportTitle}>{r.title}</div>
                              <div style={styles.reportMeta}>
                                {r.reportType} · {r.createdAt?.toDate?.().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) || '—'}
                              </div>
                            </div>
                            <button
                              style={styles.viewBtn}
                              onClick={() => navigate('/admin/reports')}
                            >
                              Fungua
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#F6F4EE' },
  main: { flex: 1, padding: '26px', overflowY: 'auto' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap', marginBottom: '18px' },
  pageTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '25px', color: '#14452F', fontWeight: '700' },
  pageSub: { fontSize: '12.5px', color: '#6B7280', marginTop: '3px', maxWidth: '520px' },
  writeBtn: { display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'linear-gradient(135deg, #14452F, #1F6B45)', color: '#fff', border: 'none', padding: '11px 18px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  summaryRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '18px' },
  summaryCard: { background: '#fff', border: '1.5px solid', borderRadius: '14px', padding: '16px 18px' },
  summaryNum: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '28px', fontWeight: '700' },
  summaryLabel: { fontSize: '12px', color: '#6B7280', marginTop: '2px' },
  controlsRow: { display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  tabRow: { display: 'flex', gap: '8px' },
  tabBtn: { display: 'inline-flex', alignItems: 'center', gap: '7px', background: '#fff', border: '1.5px solid rgba(0,0,0,0.08)', color: '#4B5563', padding: '9px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  tabBtnActive: { background: '#14452F', borderColor: '#14452F', color: '#fff' },
  filterRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  filterBtn: { background: 'transparent', border: '1.5px solid rgba(0,0,0,0.1)', color: '#6B7280', padding: '8px 13px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  filterBtnActive: { background: 'rgba(20,69,47,0.08)', borderColor: '#14452F', color: '#14452F' },
  search: { width: '100%', padding: '12px 16px', borderRadius: '11px', border: '1.5px solid #E5E7EB', background: '#fff', fontFamily: 'inherit', fontSize: '13.5px', outline: 'none', marginBottom: '14px' },
  list: { display: 'flex', flexDirection: 'column', gap: '10px', paddingBottom: '30px' },
  card: { background: '#fff', borderRadius: '14px', border: '1px solid rgba(0,0,0,0.05)', overflow: 'hidden' },
  cardHead: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '14px 18px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', flexWrap: 'wrap' },
  cardHeadLeft: { minWidth: '160px' },
  personName: { fontSize: '14.5px', fontWeight: '700', color: '#1C1C1E' },
  personMeta: { fontSize: '11.5px', color: '#9CA3AF', marginTop: '2px' },
  cardHeadRight: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  statusBadge: { fontSize: '11px', fontWeight: '700', padding: '5px 11px', borderRadius: '20px' },
  countPill: { fontSize: '11.5px', fontWeight: '700', color: '#14452F', background: 'rgba(20,69,47,0.07)', padding: '5px 11px', borderRadius: '20px' },
  lastDate: { fontSize: '11.5px', color: '#6B7280' },
  chevron: { color: '#9CA3AF', fontSize: '13px' },
  reportList: { borderTop: '1px solid #F0EEE7', padding: '6px 18px 14px' },
  noReports: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', fontSize: '12.5px', color: '#C62828', padding: '12px 0' },
  writeSmallBtn: { background: '#14452F', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: '8px', fontSize: '11.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  reportRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid #F7F5EF' },
  reportTitle: { fontSize: '13px', fontWeight: '600', color: '#1C1C1E', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  reportMeta: { fontSize: '11px', color: '#9CA3AF', marginTop: '1px' },
  viewBtn: { background: 'transparent', border: '1.5px solid #14452F', color: '#14452F', padding: '6px 14px', borderRadius: '8px', fontSize: '11.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 },
  empty: { textAlign: 'center', color: '#9CA3AF', fontSize: '13px', padding: '40px 0' },
  toolsBox: { padding: '12px 0 6px', borderBottom: '1px solid #F0EEE7', marginBottom: '4px' },
  trackBadgeRow: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '12px' },
  trackBadge: { fontSize: '11px', fontWeight: '700', padding: '5px 11px', borderRadius: '20px' },
  chartGrid: { display: 'flex', gap: '18px', flexWrap: 'wrap' },
  trackRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' },
  trackPill: { display: 'inline-flex', alignItems: 'center', gap: '7px', background: '#FAF9F4', border: '1px solid #EBE8DE', borderRadius: '20px', padding: '6px 13px' },
  trackLabel: { fontSize: '11px', fontWeight: '600', color: '#6B7280' },
  trackValue: { fontSize: '12.5px', fontWeight: '800' },
  chartsRow: { display: 'flex', gap: '18px', flexWrap: 'wrap' },
  toolsBtnRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  aiBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'linear-gradient(135deg, #14452F, #1F6B45)', color: '#fff', border: 'none', padding: '8px 15px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  printBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'transparent', border: '1.5px solid #14452F', color: '#14452F', padding: '8px 15px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  attRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '10px' },
  attLabel: { fontSize: '11.5px', color: '#6B7280', fontWeight: '600' },
  attValue: { fontSize: '12px', fontWeight: '800', color: '#14452F' },
  attWarn: { fontSize: '11px', fontWeight: '700', color: '#C62828', background: '#FFEBEE', padding: '4px 10px', borderRadius: '20px' },
  aiSummaryBox: { background: '#F4F7F4', border: '1px solid #DCE8DE', borderRadius: '10px', padding: '12px 14px', marginTop: '12px' },
  aiSummaryTitle: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '800', color: '#14452F', marginBottom: '6px' },
  aiSummaryPara: { fontSize: '12.5px', color: '#374151', lineHeight: 1.75, marginBottom: '8px' },
};
